/* eslint-disable import-x/no-internal-modules */
import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import { AppBehaviorProvider, DataProvider, WeatherProvider } from '#features';
import { Loader, routes, SentryErrorBoundary } from '#shared';

import { RouteGuard } from './RouteGuard';

// Lazy load page components for code splitting

const StartPage = lazy(() =>
  import('#pages/StartPage').then(module => ({
    default: module.StartPage,
  })),
);

const MainMapPage = lazy(() =>
  import('#pages/MainMapPage').then(module => ({
    default: module.MainMapPage,
  })),
);

const AircraftCardsPage = lazy(() =>
  import('#pages/AircraftCardsPage').then(module => ({
    default: module.AircraftCardsPage,
  })),
);

const CrewAvailabilityPage = lazy(() =>
  import('#pages/CrewAvailabilityPage').then(module => ({
    default: module.CrewAvailabilityPage,
  })),
);

const ErrorsPage = lazy(() =>
  import('#pages/ErrorsPage').then(module => ({
    default: module.ErrorsPage,
  })),
);

const StatisticsPage = lazy(() =>
  import('#pages/StatisticsPage').then(module => ({
    default: module.StatisticsPage,
  })),
);

const HubsReportPage = lazy(() =>
  import('#pages/HubsReportPage').then(module => ({
    default: module.HubsReportPage,
  })),
);

const WeatherPage = lazy(() =>
  import('#pages/WeatherPage').then(module => ({
    default: module.WeatherPage,
  })),
);

const WindMapPage = lazy(() =>
  import('#pages/WindMapPage/WindMapPage').then(module => ({
    default: module.WindMapPage,
  })),
);

const StatsPage = lazy(() =>
  import('#pages/StatsPage').then(module => ({
    default: module.StatsPage,
  })),
);

const SettingsPage = lazy(() =>
  import('#pages/SettingsPage').then(module => ({
    default: module.SettingsPage,
  })),
);

const LogoutPage = lazy(() =>
  import('#pages/LogoutPage').then(module => ({
    default: module.LogoutPage,
  })),
);

const NoAccessPage = lazy(() =>
  import('#pages/NoAccessPage').then(module => ({
    default: module.NoAccessPage,
  })),
);

const PageNotFound = lazy(() =>
  import('#pages/PageNotFound').then(module => ({
    default: module.PageNotFound,
  })),
);

export const AppRouter = () => (
  // SpecRef: ARCH-005
  // Reference: docs/decisions/ARCH-005-ui-error-detection-resilience.md#1-sentryerrorboundary-placement
  // Router-level boundary isolates page crashes from the outer shell.
  <SentryErrorBoundary showDialog={false}>
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path={routes.START_PAGE}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <StartPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.MAIN_MAP}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <AppBehaviorProvider>
                  <MainMapPage />
                </AppBehaviorProvider>
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.ERRORS}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <ErrorsPage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.STATISTICS}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <StatisticsPage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.AIRCRAFTS_MAP}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <AppBehaviorProvider>
                  <WindMapPage />
                </AppBehaviorProvider>
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.AIRCRAFTS_CARDS}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <AppBehaviorProvider>
                  <AircraftCardsPage />
                </AppBehaviorProvider>
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.CREW_AVAILABILITY}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <AppBehaviorProvider>
                  <CrewAvailabilityPage />
                </AppBehaviorProvider>
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.HUBS_REPORT}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <DataProvider>
                <HubsReportPage />
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.WEATHER}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <WeatherProvider>
                <DataProvider>
                  <WeatherPage />
                </DataProvider>
              </WeatherProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.WEATHER_WINDS}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter']}>
              <WeatherProvider>
                <DataProvider>
                  <WindMapPage />
                </DataProvider>
              </WeatherProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.SETTINGS}
          element={
            <RouteGuard ssoUserGroups={['CommandCenter', 'Statistics']}>
              <DataProvider>
                <AppBehaviorProvider>
                  <SettingsPage />
                </AppBehaviorProvider>
              </DataProvider>
            </RouteGuard>
          }
        />
        <Route
          path={routes.STATS}
          element={
            <RouteGuard ssoUserGroups={['Statistics']}>
              <StatsPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.LOGOUT}
          element={
            <RouteGuard>
              <LogoutPage />
            </RouteGuard>
          }
        />
        <Route
          path={routes.NO_ACCESS}
          element={
            <RouteGuard>
              <NoAccessPage />
            </RouteGuard>
          }
        />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  </SentryErrorBoundary>
);
